import { isHybridDeployment } from '@infra/licence/DataModeResolver';
import type { DataMode, DeploymentMode } from '@infra/licence/ActivationClaims';
import type { Role } from '@shared/types/User';

/** Version du texte de consentement (docs/legal/HYBRIDE_CONSENTEMENT_RGPD.md) enregistrée avec l'accord. */
export const HYBRID_CONSENT_VERSION = 'hybride-2026-10';

export interface HybridSyncInput {
  claims: { tenant_id: string; data_mode?: DataMode; deployment_mode?: DeploymentMode } | null;
  /** SQLite chiffré est le stockage primaire du poste. */
  sqliteIsPrimary: boolean;
  /** Le pont Electron de la file de synchronisation est disponible. */
  bridgeAvailable: boolean;
  /** Société de la session ouverte (cabinet : la société active). */
  sessionTenantId: string | null;
  sessionRole: Role | null;
  /** Identifiant utilisateur de la session et vrai si c'est un compte du SERVEUR (id serveur, auteur valable). */
  sessionUserId?: string | null;
  sessionAccountVerified?: boolean;
  /** Fiche entreprise locale (`tenants`) : son identifiant doit être celui du certificat. */
  localTenantId: string | null;
  /** Consentement déjà enregistré sur ce poste pour cette société. */
  consentRecorded: boolean;
}

export type HybridSyncEligibility =
  | { kind: 'not_applicable'; reason: 'not_hybrid' | 'no_bridge' | 'no_session' }
  | { kind: 'blocked'; reason: 'no_sqlite' | 'tenant_mismatch'; message: string }
  | { kind: 'consent_required'; canConsent: boolean; message: string }
  | { kind: 'eligible' };

/**
 * Qui peut synchroniser, et quand (SYNC_DESIGN §6.1). Standalone : jamais, aucun appel réseau.
 * Hybride : licence hybride signée + SQLite primaire + société locale = société du certificat + consentement.
 */
export function evaluateHybridSyncEligibility(input: HybridSyncInput): HybridSyncEligibility {
  if (!input.claims || !isHybridDeployment(input.claims)) return { kind: 'not_applicable', reason: 'not_hybrid' };
  if (!input.bridgeAvailable) return { kind: 'not_applicable', reason: 'no_bridge' };
  if (!input.sessionTenantId || !input.sessionRole) return { kind: 'not_applicable', reason: 'no_session' };
  if (!input.sqliteIsPrimary) {
    return {
      kind: 'blocked',
      reason: 'no_sqlite',
      message: 'La synchronisation nécessite la base locale chiffrée (SQLite), qui n’est pas active sur ce poste.',
    };
  }
  if (input.sessionTenantId !== input.claims.tenant_id || (input.localTenantId && input.localTenantId !== input.claims.tenant_id)) {
    return {
      kind: 'blocked',
      reason: 'tenant_mismatch',
      message:
        'L’identifiant de la société sur ce poste ne correspond pas à celui de la licence. Contactez le support Nexxus avant d’activer la synchronisation.',
    };
  }
  if (!input.consentRecorded) {
    const canConsent = input.sessionRole === 'admin' || input.sessionRole === 'super_admin';
    return {
      kind: 'consent_required',
      canConsent,
      message: canConsent
        ? 'Activez la synchronisation de votre offre Hybride pour retrouver vos données sur le site Nexxus.'
        : 'La synchronisation doit être activée par un administrateur de l’entreprise.',
    };
  }
  return { kind: 'eligible' };
}
